/**
 * Kleine DOM-Helfer. Bewusst kein Framework: die Oberfläche ist überschaubar,
 * und weniger Fremdcode im Umfeld der Kryptographie ist hier ein Vorteil.
 */

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: Record<string, string | boolean | EventListener> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attributes)) {
    if (typeof value === 'function') {
      node.addEventListener(key.replace(/^on/, '').toLowerCase(), value as EventListener);
    } else if (typeof value === 'boolean') {
      if (value) node.setAttribute(key, '');
    } else {
      node.setAttribute(key, value);
    }
  }

  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function hinweis(
  text: string,
  art: 'info' | 'warnung' | 'erfolg' = 'info',
): HTMLElement {
  return el('div', { class: art === 'info' ? 'hinweis' : `hinweis ${art}`, role: 'note' }, text);
}

export function karte(...children: Child[]): HTMLElement {
  return el('section', { class: 'karte' }, ...children);
}

/**
 * Info-Symbol, das seinen Text erst beim Darüberfahren zeigt.
 *
 * Erklärungen dauerhaft anzuzeigen macht die Oberfläche voll und lenkt vom
 * eigentlichen Ablauf ab. Wer die Erklärung braucht, findet sie; wer sie
 * kennt, sieht nur ein kleines Zeichen.
 *
 * Erreichbar ist der Text auch per Tastatur (Tabulator) und für
 * Vorleseprogramme – sonst wäre er für einen Teil der Zielgruppe schlicht
 * nicht vorhanden.
 */
export function infoSymbol(text: string): HTMLElement {
  const id = `info-${Math.random().toString(36).slice(2, 9)}`;
  return el(
    'span',
    { class: 'info-huelle' },
    el(
      'button',
      {
        type: 'button',
        class: 'info-zeichen',
        'aria-describedby': id,
        'aria-label': 'Erklärung anzeigen',
      },
      'i',
    ),
    el('span', { class: 'info-text', id, role: 'tooltip' }, text),
  );
}

/** Datumsanzeige in der Form, die im deutschen Alltag üblich ist. */
export function formatDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Byte`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Symbolknöpfe für Ordneraktionen.
 *
 * Gezeichnet statt Emoji: Emoji sehen auf jedem Betriebssystem anders aus,
 * lassen sich nicht einfärben und wirken in einer Anwendung für Unterlagen
 * unpassend. Diese hier folgen der Schriftfarbe und bleiben bei jeder
 * Zoomstufe scharf.
 *
 * Jeder Knopf trägt eine Beschriftung für Vorleseprogramme und zeigt sie
 * beim Darüberfahren an – ein Symbol allein ist gerade für ältere Nutzer
 * nicht immer selbsterklärend.
 */
type SymbolName = 'unterordner' | 'umbenennen' | 'loeschen';

const SVG_NS = 'http://www.w3.org/2000/svg';

const SYMBOL_PFADE: Record<SymbolName, string[]> = {
  // Ordner mit Pluszeichen
  unterordner: ['M3 6.5h6l1.5 2H21v10.5H3z', 'M12 11.5v5', 'M9.5 14h5'],
  // Stift
  umbenennen: ['M4 20h4L19 9l-4-4L4 16z', 'M14.5 5.5l4 4'],
  // Papierkorb mit Deckel
  loeschen: ['M4 7h16', 'M9.5 7V4.5h5V7', 'M6 7l1 13h10l1-13', 'M10 10.5v6', 'M14 10.5v6'],
};

export function iconKnopf(
  symbol: SymbolName,
  beschriftung: string,
  beiKlick: () => void,
  gefahr = false,
): HTMLElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '19');
  svg.setAttribute('height', '19');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.9');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  // Das Symbol trägt keine eigene Bedeutung für Vorleseprogramme – die
  // steckt in der Beschriftung des Knopfes.
  svg.setAttribute('aria-hidden', 'true');

  for (const d of SYMBOL_PFADE[symbol]) {
    const pfad = document.createElementNS(SVG_NS, 'path');
    pfad.setAttribute('d', d);
    svg.append(pfad);
  }

  const knopf = el('button', {
    type: 'button',
    class: gefahr ? 'icon-knopf gefahr' : 'icon-knopf',
    'aria-label': beschriftung,
    // Native Kurzinfo beim Darüberfahren – ohne zusätzliches Gerüst.
    title: beschriftung,
    onclick: beiKlick,
  });
  knopf.append(svg);
  return knopf;
}

/**
 * Auswahl-Dialog: zeigt eine Liste, gibt das Gewählte zurück.
 *
 * Bewusst grosse, ganzflächige Schaltflächen statt eines Auswahlfelds –
 * ein aufgeklapptes Auswahlfeld ist mit zittriger Hand schwer zu treffen,
 * eine Liste mit breiten Zeilen nicht.
 *
 * Nutzt das native `dialog`-Element: Es fängt den Tastaturfokus ein, lässt
 * sich mit Escape schliessen und wird von Vorleseprogrammen korrekt
 * angekündigt – all das müsste man sonst nachbauen.
 */
export function waehleAus(
  titel: string,
  beschreibung: string,
  eintraege: Array<{ wert: string | null; text: string; aktuell?: boolean }>,
): Promise<string | null | undefined> {
  return new Promise((aufloesen) => {
    const dialog = el('dialog', { class: 'waehler' }) as HTMLDialogElement;
    let gewaehlt: string | null | undefined;

    const liste = el('ul', { class: 'zielliste' });
    for (const eintrag of eintraege) {
      liste.append(
        el(
          'li',
          {},
          el(
            'button',
            {
              type: 'button',
              ...(eintrag.aktuell ? { class: 'aktuell', disabled: true } : {}),
              onclick: () => {
                gewaehlt = eintrag.wert;
                dialog.close();
              },
            },
            eintrag.aktuell ? `${eintrag.text}  (aktuell)` : eintrag.text,
          ),
        ),
      );
    }

    dialog.append(
      el(
        'div',
        { class: 'inhalt' },
        el('h3', {}, titel),
        beschreibung ? el('p', { class: 'leise' }, beschreibung) : null,
        liste,
        el(
          'div',
          { class: 'knopfreihe' },
          el(
            'button',
            { class: 'sekundaer', type: 'button', onclick: () => dialog.close() },
            'Abbrechen',
          ),
        ),
      ),
    );

    dialog.addEventListener('close', () => {
      dialog.remove();
      aufloesen(gewaehlt);
    });

    document.body.append(dialog);
    dialog.showModal();
  });
}

/** Verbleibende Zeit einer Freigabe, in Worten. */
export function formatRemaining(expiresAt: string): string {
  const minutes = Math.round((new Date(expiresAt).getTime() - Date.now()) / 60_000);
  if (minutes <= 0) return 'abgelaufen';
  if (minutes < 60) return `noch ${minutes} Minuten`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `noch ${hours} Stunden`;
  return `noch ${Math.round(hours / 24)} Tage`;
}

/**
 * Passwortfeld mit „Anzeigen"/„Verbergen"-Knopf.
 *
 * Bewusst ein Textknopf statt eines Augen-Symbols: Die Zielgruppe soll nicht
 * raten müssen, was ein Piktogramm tut. Der Knopf wechselt nur den Feldtyp
 * (password/text) — am Wert und am Absenden ändert er nichts.
 */
export function passwortfeld(
  attributes: Record<string, string | boolean | EventListener> = {},
): HTMLElement {
  const input = el('input', { type: 'password', ...attributes });
  const knopf = el(
    'button',
    {
      type: 'button',
      class: 'sekundaer pw-anzeigen',
      'aria-pressed': 'false',
      onclick: () => {
        const anzeigen = input.type === 'password';
        input.type = anzeigen ? 'text' : 'password';
        knopf.textContent = anzeigen ? 'Verbergen' : 'Anzeigen';
        knopf.setAttribute('aria-pressed', anzeigen ? 'true' : 'false');
        input.focus();
      },
    },
    'Anzeigen',
  );
  return el('div', { class: 'pw-feld' }, input, knopf);
}

/**
 * Ablegefläche für eine oder mehrere Dateien: Ziehen, Fallenlassen, oder
 * über den Knopf auswählen.
 *
 * Warum nicht das nackte `<input type="file">`: Dessen Knopf lässt sich
 * nicht gestalten (das Aussehen bestimmt der Browser), heißt je nach System
 * anders und ist klein. Hier liegt das Feld unsichtbar hinter einer großen
 * Fläche im Markenbild — bedienbar per Maus, Finger, Tastatur und eben auch
 * per Ziehen aus dem Dateifenster.
 *
 * Gewählte Dateien werden **ergänzt**, nicht ersetzt: Wer Unterlagen aus
 * mehreren Ordnern zusammensucht, verliert sonst bei jedem zweiten Griff
 * die erste Auswahl. Doppelte (gleicher Name, Größe und Änderungszeit)
 * werden dabei stillschweigend übergangen.
 */
export interface AblegeFeld {
  /** Fläche samt Auswahlknopf – in die Karte einhängen. */
  element: HTMLElement;
  /** Liste der noch nicht abgelegten Dateien, in Auswahlreihenfolge. */
  dateien(): File[];
  /** Auswahl vollständig verwerfen. */
  leeren(): void;
  /**
   * Einzelne Dateien aus der Auswahl nehmen – die tatsächlich abgelegten.
   *
   * Alles stehen zu lassen wäre gefährlich: Ein zweiter Klick auf „Ablegen"
   * würde die bereits abgelegten Dokumente ein weiteres Mal hochladen.
   * Alles zu leeren wäre lästig: Wer bei Nummer 17 einen Fehler hatte,
   * müsste die Datei im Dateifenster neu heraussuchen.
   */
  entferne(dateien: File[]): void;
  /** Auswahl vorbelegen (etwa der Rest nach einem Lauf mit Fehlern). */
  uebernimm(dateien: File[]): void;
  /**
   * Während des Ablegens ausblenden.
   *
   * Sonst stünden Auswahlliste und Warteschlange untereinander und zeigten
   * dasselbe zweimal – bei zwanzig Dateien eine sehr lange Seite. Nebenbei
   * verhindert es, dass mitten im Lauf noch etwas hinzukommt oder
   * verschwindet, während die Reihenfolge längst feststeht.
   */
  sperren(an: boolean): void;
  /** Wird nach jeder Änderung der Auswahl gerufen. */
  beiAenderung(fn: (dateien: File[]) => void): void;
}

export function ablegeFeld(options: {
  id: string;
  accept: string;
  /** Beschriftung über der Fläche. */
  beschriftung: string;
  /** Sichtbare Aufforderung in der Fläche. */
  aufforderung?: string;
  /** Erklärtext unter der Fläche. */
  erklaerung?: string;
}): AblegeFeld {
  const {
    id,
    accept,
    beschriftung,
    aufforderung = 'Dateien hierher ziehen',
    erklaerung,
  } = options;

  let gewaehlt: File[] = [];
  const horcher: Array<(dateien: File[]) => void> = [];

  const kennung = (datei: File): string =>
    `${datei.name}|${datei.size}|${datei.lastModified}`;

  const eingabe = el('input', {
    type: 'file',
    id,
    accept,
    multiple: true,
    class: 'datei-eingabe',
  }) as HTMLInputElement;

  const liste = el('ul', { class: 'dateiliste' });

  const knopf = el(
    'button',
    {
      type: 'button',
      class: 'sekundaer',
      onclick: () => eingabe.click(),
    },
    'Dateien auswählen',
  );

  const flaeche = el(
    'div',
    { class: 'ablegeflaeche' },
    el('p', { class: 'ablege-text' }, aufforderung),
    el('p', { class: 'leise ablege-oder' }, 'oder'),
    knopf,
    eingabe,
  );

  function melde(): void {
    for (const fn of horcher) fn([...gewaehlt]);
  }

  function zeichneListe(): void {
    clear(liste);
    if (gewaehlt.length === 0) return;

    liste.append(
      el(
        'li',
        { class: 'dateiliste-kopf' },
        el(
          'span',
          {},
          gewaehlt.length === 1
            ? '1 Datei ausgewählt'
            : `${gewaehlt.length} Dateien ausgewählt`,
        ),
        el(
          'button',
          {
            type: 'button',
            class: 'sekundaer schmal',
            onclick: () => {
              gewaehlt = [];
              zeichneListe();
              melde();
            },
          },
          'Alle entfernen',
        ),
      ),
    );

    for (const datei of gewaehlt) {
      liste.append(
        el(
          'li',
          {},
          el('span', { class: 'dateiname' }, datei.name),
          el('span', { class: 'leise' }, formatBytes(datei.size)),
          el(
            'button',
            {
              type: 'button',
              class: 'sekundaer schmal',
              'aria-label': `„${datei.name}" aus der Auswahl entfernen`,
              onclick: () => {
                gewaehlt = gewaehlt.filter((d) => d !== datei);
                zeichneListe();
                melde();
              },
            },
            'Entfernen',
          ),
        ),
      );
    }
  }

  function uebernimm(neue: FileList | File[] | null): void {
    if (!neue) return;
    const vorhanden = new Set(gewaehlt.map(kennung));
    for (const datei of Array.from(neue)) {
      if (vorhanden.has(kennung(datei))) continue;
      vorhanden.add(kennung(datei));
      gewaehlt.push(datei);
    }
    zeichneListe();
    melde();
  }

  eingabe.addEventListener('change', () => {
    uebernimm(eingabe.files);
    // Zurücksetzen, damit dieselbe Datei nach dem Entfernen erneut gewählt
    // werden kann – ohne das löst `change` beim zweiten Mal nicht aus.
    eingabe.value = '';
  });

  // Klick auf die Fläche selbst öffnet ebenfalls den Dateidialog. Der Knopf
  // ist der sichtbare Weg, die ganze Fläche der bequeme.
  flaeche.addEventListener('click', (ereignis) => {
    if (ereignis.target === flaeche || (ereignis.target as HTMLElement).closest('.ablege-text')) {
      eingabe.click();
    }
  });

  for (const name of ['dragenter', 'dragover']) {
    flaeche.addEventListener(name, (ereignis) => {
      ereignis.preventDefault();
      flaeche.classList.add('darueber');
    });
  }
  for (const name of ['dragleave', 'dragend']) {
    flaeche.addEventListener(name, (ereignis) => {
      // `dragleave` feuert auch beim Überfahren von Kindelementen. Nur wenn
      // der Zeiger die Fläche wirklich verlässt, die Hervorhebung nehmen.
      if (ereignis.target !== flaeche) return;
      flaeche.classList.remove('darueber');
    });
  }
  flaeche.addEventListener('drop', (ereignis) => {
    ereignis.preventDefault();
    flaeche.classList.remove('darueber');
    uebernimm((ereignis as DragEvent).dataTransfer?.files ?? null);
  });

  // Ohne das lädt der Browser eine fallengelassene Datei einfach im Fenster,
  // sobald jemand knapp neben die Fläche trifft – die Anwendung wäre weg,
  // samt entsperrtem Schlüssel.
  const abwehren = (ereignis: Event): void => ereignis.preventDefault();
  window.addEventListener('dragover', abwehren);
  window.addEventListener('drop', abwehren);

  // Beschriftung und Erklärung gehören mit in die Hülle: Beim Ablegen wird
  // sie als Ganzes ausgeblendet, und ein „Dateien auswählen"-Label über
  // einer Warteschlange, die schon läuft, wäre eine Aufforderung ins Leere.
  const huelle = el(
    'div',
    { class: 'ablege-huelle' },
    el('label', { for: id }, beschriftung),
    flaeche,
    liste,
    erklaerung ? el('p', { class: 'leise' }, erklaerung) : null,
  );

  return {
    element: huelle,
    dateien: () => [...gewaehlt],
    leeren: () => {
      gewaehlt = [];
      zeichneListe();
      melde();
    },
    entferne: (weg) => {
      const raus = new Set(weg.map(kennung));
      gewaehlt = gewaehlt.filter((datei) => !raus.has(kennung(datei)));
      zeichneListe();
      melde();
    },
    uebernimm: (neue) => uebernimm(neue),
    sperren: (an) => {
      huelle.classList.toggle('gesperrt', an);
      eingabe.disabled = an;
    },
    beiAenderung: (fn) => {
      horcher.push(fn);
    },
  };
}
